import { supabase, isConfigured, storePendingMarketingConsent, checkRateLimits, logEvent, isAbortError } from '@rehub/supabase';
import { checkAdminStatus as checkAdminStatusShared, getAdminRole as getAdminRoleShared } from '@rehub/admin';
import {
  DEFAULT_HYPER_DUSK_ANIMATION_STYLE,
  DEFAULT_HYPER_DUSK_MOTION_PROMPT,
  DEFAULT_HYPER_DUSK_NANO_PROMPT,
  DEFAULT_MOTION_PROMPT,
  SERVICES,
  type AspectRatio,
  type HyperDuskAnimationStyle,
  type HyperDuskVariant,
  type ImagemotionProject,
  type ImagemotionProjectImage,
  type ImagemotionQuality,
  type ImagemotionServiceType,
  type ProjectSettings,
} from '../types';

// Re-export shared client & auth helpers
export { supabase, isConfigured, storePendingMarketingConsent, checkRateLimits, logEvent, isAbortError };

// Stripe billing portal (invoices, payment methods, subscriptions). Shared
// across every ReHub app via @rehub/commerce + the single Supabase project.
import { openBillingPortal as openBillingPortalShared } from '@rehub/commerce';
export const openBillingPortal = (returnUrl?: string) =>
  openBillingPortalShared(supabase, returnUrl);

// --- Imagemotion-specific Storage ---

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === 'object') {
    const maybe = error as { message?: string; details?: string; hint?: string; code?: string };
    const parts = [maybe.message, maybe.details, maybe.hint].filter(Boolean);
    if (parts.length) return parts.join(' | ');
    if (maybe.code) return `${fallback} (${maybe.code})`;
  }
  return fallback;
}

export async function getFunctionInvokeErrorMessage(error: unknown, fallback: string): Promise<string> {
  if (!error || typeof error !== 'object') {
    return getErrorMessage(error, fallback);
  }

  const maybe = error as { context?: Response };
  if (maybe.context && typeof maybe.context.json === 'function') {
    try {
      const payload = await maybe.context.clone().json() as { error?: string; message?: string; details?: string };
      const parts = [payload.error, payload.message, payload.details].filter(Boolean);
      if (parts.length) return parts.join(' | ');
    } catch {
      // ignore JSON parse issues and fall through
    }
  }

  return getErrorMessage(error, fallback);
}

function normalizeDuskVariants(value: unknown): HyperDuskVariant[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const raw = item as Record<string, unknown>;
      if (typeof raw.id !== 'string' || typeof raw.path !== 'string') return null;
      return {
        id: raw.id,
        path: raw.path,
        url: typeof raw.url === 'string' ? raw.url : null,
        created_at: typeof raw.created_at === 'string' ? raw.created_at : new Date().toISOString(),
        preset_id: typeof raw.preset_id === 'string' ? raw.preset_id : null,
        animation_style: typeof raw.animation_style === 'string' ? raw.animation_style as HyperDuskAnimationStyle : null,
        nano_request_id: typeof raw.nano_request_id === 'string' ? raw.nano_request_id : null,
      } satisfies HyperDuskVariant;
    })
    .filter((v): v is HyperDuskVariant => !!v);
}

export async function uploadToStorage(
  file: File,
  folder: 'images' | 'music' | 'logos',
  userId: string
): Promise<{ url: string; path: string }> {
  const ext = file.name.split('.').pop() || 'bin';
  const storagePath = `${folder}/${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { data, error } = await supabase.storage
    .from('imagemotion-uploads')
    .upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) throw error;

  const { data: signedData, error: signError } = await supabase.storage
    .from('imagemotion-uploads')
    .createSignedUrl(data.path, 60 * 60 * 24 * 7); // 7 days

  if (signError || !signedData?.signedUrl) {
    throw signError ?? new Error('Failed to create signed URL');
  }

  return { url: signedData.signedUrl, path: data.path };
}

// --- Saved Logos ---

export interface SavedLogo {
  id: string;
  user_id: string;
  name: string | null;
  logo_url: string;
  logo_path: string;
  is_default: boolean;
  created_at: string;
}

export async function uploadAndSaveLogo(
  file: File,
  userId: string,
  name?: string
): Promise<{ url: string; savedLogo: SavedLogo | null }> {
  const ext = file.name.split('.').pop() || 'png';
  const path = `logos/${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { data, error } = await supabase.storage
    .from('imagemotion-uploads')
    .upload(path, file, { cacheControl: '31536000', upsert: false });

  if (error) throw error;

  const { data: signedData, error: signError } = await supabase.storage
    .from('imagemotion-uploads')
    .createSignedUrl(data.path, 60 * 60 * 24 * 365);

  if (signError || !signedData?.signedUrl) {
    throw signError ?? new Error('Failed to create signed URL');
  }

  // Try to persist in saved logos library; gracefully degrade if table doesn't exist yet
  const { data: logoRow, error: insertErr } = await supabase
    .from('user_saved_logos')
    .insert({
      user_id: userId,
      name: name || file.name.replace(/\.[^.]+$/, ''),
      logo_url: signedData.signedUrl,
      logo_path: path,
    })
    .select()
    .single();

  if (insertErr) {
    console.warn('Could not save to logos library:', insertErr.message);
    return { url: signedData.signedUrl, savedLogo: null };
  }

  return { url: signedData.signedUrl, savedLogo: logoRow as SavedLogo };
}

export async function fetchSavedLogos(userId: string): Promise<SavedLogo[]> {
  const { data, error } = await supabase
    .from('user_saved_logos')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) return [];

  const logos = (data ?? []) as SavedLogo[];
  for (const logo of logos) {
    const fresh = await getFreshVideoUrl(logo.logo_path, logo.logo_url);
    if (fresh) logo.logo_url = fresh;
  }
  return logos;
}

export async function deleteSavedLogo(logoId: string): Promise<void> {
  const { error } = await supabase
    .from('user_saved_logos')
    .delete()
    .eq('id', logoId);
  if (error) throw error;
}

export async function setDefaultLogo(logoId: string): Promise<void> {
  await supabase.rpc('set_default_logo', { p_logo_id: logoId });
}

// --- Imagemotion-specific Data ---

export async function fetchProjects(userId: string): Promise<
  (ImagemotionProject & { image_count: number; thumbnail_url: string | null })[]
> {
  const { data } = await supabase
    .from('imagemotion_projects')
    .select('*, imagemotion_project_images(image_url, image_path, order_index)')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  const projects = (data ?? []).map((p: any) => {
    const images = p.imagemotion_project_images ?? [];
    const sorted = [...images].sort(
      (a: any, b: any) => a.order_index - b.order_index
    );
    return {
      ...p,
      image_count: images.length,
      thumbnail_url: sorted[0]?.image_url ?? null,
      _thumbnail_path: sorted[0]?.image_path ?? null,
    };
  });

  const refreshed = await Promise.all(
    projects.map(async (p: any) => {
      if (p.thumbnail_url) {
        const fresh = await getFreshVideoUrl(p._thumbnail_path, p.thumbnail_url);
        if (fresh) p.thumbnail_url = fresh;
      }
      delete p._thumbnail_path;
      return p;
    })
  );

  return refreshed as (ImagemotionProject & { image_count: number; thumbnail_url: string | null })[];
}

export async function fetchProject(
  projectId: string
): Promise<{ project: ImagemotionProject | null; images: ImagemotionProjectImage[] }> {
  const { data, error } = await supabase
    .from('imagemotion_projects')
    .select('*')
    .eq('id', projectId)
    .single();

  if (error || !data) return { project: null, images: [] };

  const { data: imgData } = await supabase
    .from('imagemotion_project_images')
    .select('*')
    .eq('project_id', projectId)
    .order('order_index', { ascending: true });

  const project = data as ImagemotionProject;

  if (project.status === 'completed' && (project.final_video_path || project.final_video_url)) {
    const freshUrl = await getFreshVideoUrl(project.final_video_path, project.final_video_url);
    if (freshUrl) project.final_video_url = freshUrl;
  }

  const images = (imgData ?? []) as ImagemotionProjectImage[];
  await Promise.all(
    images.map(async (img) => {
      const fresh = await getFreshVideoUrl(img.image_path, img.image_url);
      if (fresh) img.image_url = fresh;

      const freshClip = await getFreshVideoUrl(img.clip_path ?? null, img.clip_url);
      if (freshClip) img.clip_url = freshClip;

      const freshPending = await getFreshVideoUrl(img.pending_clip_path ?? null, img.pending_clip_url ?? null);
      if (freshPending) img.pending_clip_url = freshPending;

      if (img.dusk_image_path || img.dusk_image_url) {
        const freshDusk = await getFreshVideoUrl(img.dusk_image_path ?? null, img.dusk_image_url ?? null);
        if (freshDusk) img.dusk_image_url = freshDusk;
      }

      const variants = normalizeDuskVariants((img as any).dusk_variants);
      if (variants.length) {
        const refreshedVariants = await Promise.all(
          variants.map(async (variant) => {
            const variantUrl = await getFreshVideoUrl(variant.path, variant.url ?? null);
            return {
              ...variant,
              url: variantUrl ?? variant.url ?? null,
            };
          })
        );
        img.dusk_variants = refreshedVariants;
      } else {
        img.dusk_variants = [];
      }
    })
  );

  return { project, images };
}

// --- Credits ---

export async function fetchUserCredits(userId: string): Promise<number> {
  const { data, error } = await supabase
    .from('profiles')
    .select('credits')
    .eq('id', userId)
    .single();

  if (error || !data) return 0;
  return data.credits ?? 0;
}

export async function consumeCreditsForProject(
  userId: string,
  projectId: string,
  credits: number
): Promise<{
  success: boolean;
  error?: string;
  credits_remaining?: number;
  required_credits?: number;
}> {
  const { data, error } = await supabase.rpc('consume_credits_for_imagemotion', {
    p_user_id: userId,
    p_project_id: projectId,
    p_credits: credits,
  });

  if (error) {
    if (error.message.includes('INSUFFICIENT_CREDITS')) {
      return { success: false, error: 'INSUFFICIENT_CREDITS' };
    }
    return { success: false, error: error.message };
  }

  const result = data as {
    success: boolean;
    error?: string;
    credits_remaining?: number;
    credits_required?: number;
  };
  return {
    ...result,
    required_credits: result.credits_required,
  };
}

let quoteRpcMissing = false;

function isMissingRpcError(error: { message?: string; code?: string } | null): boolean {
  if (!error) return false;
  return error.code === 'PGRST202' || (error.message ?? '').includes('Could not find the function');
}

export async function quoteImagemotionGeneration(input: Record<string, unknown>): Promise<{
  success: boolean;
  error?: string;
  quote?: Record<string, unknown>;
}> {
  if (quoteRpcMissing) {
    return { success: false, error: 'QUOTE_RPC_MISSING' };
  }
  try {
    const { data, error } = await supabase.rpc('quote_imagemotion_generation', {
      p_input: input,
    });
    if (error) {
      if (isAbortError(error)) return { success: false, error: 'ABORTED' };
      if (isMissingRpcError(error)) {
        quoteRpcMissing = true;
        return { success: false, error: 'QUOTE_RPC_MISSING' };
      }
      return { success: false, error: error.message };
    }
    return { success: true, quote: (data ?? {}) as Record<string, unknown> };
  } catch (err) {
    if (isAbortError(err)) return { success: false, error: 'ABORTED' };
    return { success: false, error: err instanceof Error ? err.message : 'QUOTE_FAILED' };
  }
}

export async function consumeCreditsForImagemotionUsage(params: {
  userId: string;
  projectId: string;
  credits: number;
  imageCount: number;
  kind: 'generate' | 'rework' | 'extra_export';
  input: Record<string, unknown>;
  idempotencyKey: string;
  legacyTierId?: string | null;
}): Promise<{
  success: boolean;
  error?: string;
  credits_remaining?: number;
  required_credits?: number;
  quote?: Record<string, unknown>;
}> {
  let data: unknown;
  let error: { message?: string } | null = null;
  try {
    const result = await supabase.rpc('consume_credits_for_imagemotion_usage', {
      p_user_id: params.userId,
      p_project_id: params.projectId,
      p_credits: params.credits,
      p_image_count: params.imageCount,
      p_kind: params.kind,
      p_input: params.input,
      p_idempotency_key: params.idempotencyKey,
      p_legacy_tier_id: params.legacyTierId ?? null,
    });
    data = result.data;
    error = result.error;
  } catch (err) {
    if (isAbortError(err)) return { success: false, error: 'ABORTED' };
    return { success: false, error: err instanceof Error ? err.message : 'USAGE_RPC_FAILED' };
  }
  if (error) {
    if (isAbortError(error)) return { success: false, error: 'ABORTED' };
    if ((error.message ?? '').includes('INSUFFICIENT_CREDITS')) {
      return { success: false, error: 'INSUFFICIENT_CREDITS' };
    }
    if ((error.message ?? '').includes('Could not find the function')) {
      return { success: false, error: 'USAGE_RPC_MISSING' };
    }
    return { success: false, error: error.message };
  }
  const result = (data ?? {}) as {
    success?: boolean;
    error?: string;
    credits_remaining?: number;
    required_credits?: number;
    credits_required?: number;
    quote?: Record<string, unknown>;
  };
  return {
    success: Boolean(result.success),
    error: result.error,
    credits_remaining: result.credits_remaining,
    // Package/tier RPCs return credits_required; usage path returns required_credits.
    required_credits: result.required_credits ?? result.credits_required,
    quote: result.quote,
  };
}

/** Charge flat credits from an active motion-tier config (whole project price). */
export async function consumeCreditsForMotionTier(params: {
  userId: string;
  projectId: string;
  tierId: string;
  credits: number;
  imageCount: number;
}): Promise<{
  success: boolean;
  error?: string;
  credits_remaining?: number;
  required_credits?: number;
}> {
  const { data, error } = await supabase.rpc('consume_credits_for_imagemotion_motion_tier', {
    p_user_id: params.userId,
    p_project_id: params.projectId,
    p_tier_id: params.tierId,
    p_credits: params.credits,
    p_image_count: params.imageCount,
  });

  if (error) {
    if (error.message.includes('INSUFFICIENT_CREDITS')) {
      return { success: false, error: 'INSUFFICIENT_CREDITS' };
    }
    return { success: false, error: error.message };
  }

  const result = data as {
    success: boolean;
    error?: string;
    credits_remaining?: number;
    credits_required?: number;
  };
  return {
    ...result,
    required_credits: result.credits_required,
  };
}

/** Submit / poll clip generation via the tier-resolved server adapter. */
export async function invokeGenerateMotion(params: {
  projectId: string;
  tierId?: string;
  projectImageId?: string;
  prompt?: string;
  instructions?: string;
  action?: 'submit' | 'poll' | 'rework';
}): Promise<{ success: boolean; error?: string; results?: unknown; already_running?: boolean }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    return { success: false, error: 'You must be signed in to rework a clip.' };
  }

  const body = {
    projectId: params.projectId,
    tierId: params.tierId,
    projectImageId: params.projectImageId,
    prompt: params.prompt,
    instructions: params.instructions,
    action: params.action ?? 'submit',
  };

  const invokeOnce = () =>
    supabase.functions.invoke('imagemotion-generate-motion', {
      body,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

  let { data, error } = await invokeOnce();
  if (error && /Failed to send a request/i.test(error.message ?? '')) {
    ({ data, error } = await invokeOnce());
  }

  if (error) {
    const parsed = await getFunctionInvokeErrorMessage(error, 'Motion generation failed');
    return {
      success: false,
      error: /Failed to send a request/i.test(parsed)
        ? 'Could not reach the motion service. Refresh and try again.'
        : parsed,
    };
  }

  const row = (data ?? {}) as {
    success?: boolean;
    error?: string;
    results?: unknown;
    already_running?: boolean;
  };
  if (row.success === false) {
    return { success: false, error: row.error || 'Motion generation failed' };
  }
  return { success: true, results: row.results, already_running: row.already_running };
}

export interface ImagemotionActivationState {
  success: boolean;
  credits?: number;
  tester_eligible: boolean;
  tester_status: 'pending' | 'completed' | null;
  tester_project_id: string | null;
  double_credit_eligible: boolean;
  double_credit_redeemed: boolean;
  double_credit_offer_tier: string | null;
  is_new_user: boolean;
  error?: string;
}

export async function fetchImagemotionActivationState(
  userId?: string
): Promise<ImagemotionActivationState> {
  const { data, error } = await supabase.rpc('get_imagemotion_activation_state', {
    p_user_id: userId ?? null,
  });

  if (error || !data) {
    return {
      success: false,
      tester_eligible: false,
      tester_status: null,
      tester_project_id: null,
      double_credit_eligible: false,
      double_credit_redeemed: false,
      double_credit_offer_tier: null,
      is_new_user: false,
      error: error?.message || 'FAILED',
    };
  }

  const row = data as Record<string, unknown>;
  return {
    success: Boolean(row.success),
    credits: typeof row.credits === 'number' ? row.credits : undefined,
    tester_eligible: Boolean(row.tester_eligible),
    tester_status: (row.tester_status as ImagemotionActivationState['tester_status']) ?? null,
    tester_project_id: (row.tester_project_id as string) ?? null,
    double_credit_eligible: Boolean(row.double_credit_eligible),
    double_credit_redeemed: Boolean(row.double_credit_redeemed),
    double_credit_offer_tier: (row.double_credit_offer_tier as string) ?? null,
    is_new_user: Boolean(row.is_new_user),
    error: typeof row.error === 'string' ? row.error : undefined,
  };
}

export async function completeImagemotionTesterIfApplicable(projectId: string): Promise<void> {
  await supabase.rpc('complete_imagemotion_tester_if_applicable', {
    p_project_id: projectId,
  });
}

// --- Project Update ---

export async function updateProjectName(projectId: string, name: string): Promise<void> {
  const { data, error } = await supabase
    .from('imagemotion_projects')
    .update({ name: name || null, updated_at: new Date().toISOString() })
    .eq('id', projectId)
    .select('id');

  if (error) throw error;
  if (!data || data.length === 0) throw new Error('Update failed — no rows affected');
}

/** Stop the legacy 2.1 clip worker from picking up an unpaid / unclaimed project. */
export async function failProject(projectId: string, errorMessage: string): Promise<void> {
  const { error } = await supabase
    .from('imagemotion_projects')
    .update({
      status: 'failed',
      error_message: errorMessage,
      updated_at: new Date().toISOString(),
    })
    .eq('id', projectId)
    .in('status', ['queued', 'editing']);

  if (error) {
    console.warn('failProject:', error.message);
  }
}

// --- Project Deletion ---

export async function deleteProject(projectId: string): Promise<void> {
  // Delete images first (FK constraint)
  const { error: imgError } = await supabase
    .from('imagemotion_project_images')
    .delete()
    .eq('project_id', projectId);

  if (imgError) throw imgError;

  const { error: projError } = await supabase
    .from('imagemotion_projects')
    .delete()
    .eq('id', projectId);

  if (projError) throw projError;
}

// --- Retry / Render ---

export async function retryProject(projectId: string): Promise<void> {
  const { error } = await supabase
    .from('imagemotion_projects')
    .update({
      status: 'queued',
      error_message: null,
      shotstack_render_id: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', projectId);

  if (error) throw error;
}

export async function invokeRender(
  projectId: string,
  options?: { idempotencyKey?: string; editState?: Record<string, unknown> },
): Promise<{ success: boolean; already_running?: boolean; url?: string; error?: string }> {
  const { data, error } = await supabase.functions.invoke('imagemotion-render', {
    body: {
      projectId,
      idempotencyKey: options?.idempotencyKey,
      editState: options?.editState,
    },
  });
  if (error) {
    return {
      success: false,
      error: await getFunctionInvokeErrorMessage(error, 'Render failed'),
    };
  }
  const row = (data ?? {}) as {
    success?: boolean;
    already_running?: boolean;
    url?: string;
    error?: string;
  };
  if (row.success === false) {
    return { success: false, error: row.error || 'Render failed' };
  }
  return {
    success: true,
    already_running: row.already_running,
    url: row.url,
  };
}

/** Ask the server to copy a finished Shotstack render into storage, if it is ready. */
export async function pollImagemotionRender(projectId: string): Promise<void> {
  const { error } = await supabase.functions.invoke('imagemotion-render-callback', {
    body: { action: 'poll', projectId },
  });
  if (error) {
    console.warn('Render poll error:', error);
  }
}

export async function saveProjectEdit(
  projectId: string,
  editState: Record<string, unknown>,
): Promise<{ success: boolean; revision?: number; error?: string }> {
  const { data, error } = await supabase.rpc('save_imagemotion_edit', {
    p_project_id: projectId,
    p_edit_state: editState,
  });
  if (error) return { success: false, error: error.message };
  const row = (data ?? {}) as { success?: boolean; revision?: number; error?: string };
  if (row.success === false) return { success: false, error: row.error || 'Save failed' };
  return { success: true, revision: row.revision };
}

export async function acceptPendingClip(imageId: string, current: {
  clip_url: string | null;
  clip_path?: string | null;
  clip_duration: number;
  clip_versions?: ImagemotionProjectImage['clip_versions'];
  pending_clip_url?: string | null;
  pending_clip_path?: string | null;
  pending_clip_duration?: number | null;
  pending_generation_id?: string | null;
}): Promise<void> {
  if (!current.pending_clip_url) return;
  const history = Array.isArray(current.clip_versions) ? [...current.clip_versions] : [];
  if (current.clip_url) {
    history.unshift({
      id: current.pending_generation_id || `${imageId}-prev-${Date.now()}`,
      clipUrl: current.clip_url,
      clipPath: current.clip_path ?? null,
      duration: current.clip_duration,
      createdAt: new Date().toISOString(),
      generationId: null,
    });
  }
  const { error } = await supabase
    .from('imagemotion_project_images')
    .update({
      clip_url: current.pending_clip_url,
      clip_path: current.pending_clip_path ?? null,
      clip_duration: current.pending_clip_duration ?? current.clip_duration,
      clip_versions: history,
      pending_clip_url: null,
      pending_clip_path: null,
      pending_clip_duration: null,
      pending_generation_id: null,
    })
    .eq('id', imageId);
  if (error) throw error;
}

export async function discardPendingClip(imageId: string): Promise<void> {
  const { error } = await supabase
    .from('imagemotion_project_images')
    .update({
      pending_clip_url: null,
      pending_clip_path: null,
      pending_clip_duration: null,
      pending_generation_id: null,
    })
    .eq('id', imageId);
  if (error) throw error;
}

export async function fetchProjectRenders(projectId: string): Promise<Array<{
  id: string;
  edit_revision: number;
  status: string;
  final_video_url: string | null;
  created_at: string;
  completed_at: string | null;
  error_message: string | null;
}>> {
  const { data, error } = await supabase
    .from('imagemotion_renders')
    .select('id, edit_revision, status, final_video_url, created_at, completed_at, error_message')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
    .limit(12);
  if (error) return [];
  return data ?? [];
}

export async function invokeHyperDuskGeneration(projectId: string): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error('You must be logged in to start Hyper Dusk generation');
  }

  const { error } = await supabase.functions.invoke('imagemotion-hyperdusk-generate', {
    body: { projectId, phase: 'dusk_preview' },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (error) throw new Error(await getFunctionInvokeErrorMessage(error, 'Failed to invoke Hyper Dusk generation'));
}

export async function generateHyperDuskPreview(
  projectId: string,
  options?: {
    presetId?: string;
    nanoPrompt?: string;
    klingPromptBase?: string;
    animationStyle?: HyperDuskAnimationStyle;
    sceneType?: string;
  }
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error('You must be logged in to generate Hyper Dusk preview');
  }

  const { error } = await supabase.functions.invoke('imagemotion-hyperdusk-generate', {
    body: { projectId, phase: 'dusk_preview', ...options },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (error) throw new Error(await getFunctionInvokeErrorMessage(error, 'Failed to generate Hyper Dusk preview'));
}

export async function generateHyperDuskAnimation(
  projectId: string,
  options?: {
    durationSeconds?: number;
    animationStyle?: HyperDuskAnimationStyle;
    selectedVariantId?: string;
  }
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) {
    throw new Error('You must be logged in to generate Hyper Dusk animation');
  }

  const { data: readinessRows, error: readinessError } = await supabase
    .from('imagemotion_project_images')
    .select('id, generation_status')
    .eq('project_id', projectId);

  if (readinessError) {
    throw new Error(getErrorMessage(readinessError, 'Failed to validate Hyper Dusk approval state'));
  }

  const hasApprovedVariant = (readinessRows ?? []).some((row: any) => row.generation_status === 'dusk_ready');
  if (!hasApprovedVariant) {
    throw new Error('Please approve a dusk image first, then generate the video.');
  }

  const { error } = await supabase.functions.invoke('imagemotion-hyperdusk-generate', {
    body: { projectId, phase: 'animation', ...options },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (error) throw new Error(await getFunctionInvokeErrorMessage(error, 'Failed to generate Hyper Dusk animation'));
}

export async function approveHyperDuskPreview(
  projectId: string,
  imageId?: string,
  selectedVariantId?: string
): Promise<void> {
  const baseQuery = supabase
    .from('imagemotion_project_images')
    .select('id, dusk_variants')
    .eq('project_id', projectId);

  const { data: rows, error: fetchError } = imageId
    ? await baseQuery.eq('id', imageId)
    : await baseQuery;

  if (fetchError) throw fetchError;
  const targetRow = (rows ?? [])[0] as { id: string; dusk_variants?: unknown } | undefined;

  let selectedPath: string | null = null;
  let selectedUrl: string | null = null;
  if (selectedVariantId && targetRow) {
    const variants = normalizeDuskVariants(targetRow.dusk_variants);
    const selectedVariant = variants.find((variant) => variant.id === selectedVariantId);
    if (!selectedVariant) throw new Error('Selected dusk variant not found');
    selectedPath = selectedVariant.path;
    selectedUrl = selectedVariant.url ?? null;
  }

  const updatePayload: Record<string, unknown> = {
    generation_status: 'dusk_ready',
    generation_error: null,
  };
  if (selectedVariantId) {
    updatePayload.selected_dusk_variant_id = selectedVariantId;
    updatePayload.dusk_image_path = selectedPath;
    if (selectedUrl) updatePayload.dusk_image_url = selectedUrl;
  }

  let updateQuery = supabase
    .from('imagemotion_project_images')
    .update(updatePayload)
    .eq('project_id', projectId);
  if (imageId) updateQuery = updateQuery.eq('id', imageId);

  const { error } = await updateQuery;
  if (error) throw error;
}

// --- Download ---

const STORAGE_BUCKET = 'imagemotion-uploads';

/**
 * Extracts the storage object path from a Supabase signed URL.
 * Signed URLs follow the pattern:
 *   .../storage/v1/object/sign/{bucket}/{path}?token=...
 */
function extractStoragePath(signedUrl: string): string | null {
  try {
    const url = new URL(signedUrl);
    const marker = `/object/sign/${STORAGE_BUCKET}/`;
    const idx = url.pathname.indexOf(marker);
    if (idx === -1) return null;
    return decodeURIComponent(url.pathname.slice(idx + marker.length));
  } catch {
    return null;
  }
}

export type DownloadVideoOptions = {
  storagePath?: string | null;
  projectId?: string;
};

function triggerBlobDownload(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  }, 2000);
}

function triggerDirectLink(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => document.body.removeChild(a), 2000);
}

/**
 * Copies a Shotstack/S3 (or other external) final video into our storage
 * and returns a same-origin signed URL the browser can download.
 */
export async function ingestProjectVideo(
  projectId: string
): Promise<{ path: string; signedUrl: string } | null> {
  const { data, error } = await supabase.functions.invoke('imagemotion-ingest-video', {
    body: { projectId },
  });
  if (error || !data?.signedUrl || !data?.path) return null;
  return { path: data.path as string, signedUrl: data.signedUrl as string };
}

export async function downloadVideoBlob(
  signedUrl: string,
  options?: DownloadVideoOptions
): Promise<Blob> {
  const storagePath = options?.storagePath || extractStoragePath(signedUrl);

  if (storagePath) {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .download(storagePath);
    if (!error && data) return data;
  }

  // Shotstack S3 (and similar CDNs) omit CORS headers, so a browser fetch
  // of the raw MP4 fails. Copy into our bucket first, then download.
  if (options?.projectId && !extractStoragePath(signedUrl)) {
    const ingested = await ingestProjectVideo(options.projectId);
    if (ingested?.path) {
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .download(ingested.path);
      if (!error && data) return data;
      signedUrl = ingested.signedUrl;
    }
  }

  const res = await fetch(signedUrl);
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  return res.blob();
}

/**
 * Downloads a video and triggers a browser "Save as" via a temporary anchor.
 *
 * Note: the object URL is revoked on a delay rather than synchronously after
 * click(). Revoking immediately can cancel the download of large blobs before
 * the browser has finished reading them, which manifests as "nothing happens".
 */
export async function downloadVideoFile(
  signedUrl: string,
  filename: string,
  options?: DownloadVideoOptions
): Promise<void> {
  try {
    const blob = await downloadVideoBlob(signedUrl, options);
    triggerBlobDownload(blob, filename);
  } catch (err) {
    // Last resort: open the public file so the user can still save it.
    triggerDirectLink(signedUrl, filename);
    console.warn('Blob download failed, opened video in a new tab:', err);
  }
}

/**
 * Generate a fresh signed URL from a storage path.
 * Falls back to the original URL if the path is not available.
 */
export async function getFreshVideoUrl(
  storagePath: string | null,
  fallbackUrl: string | null
): Promise<string | null> {
  if (storagePath) {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(storagePath, 60 * 60 * 24 * 7);
    if (!error && data?.signedUrl) return data.signedUrl;
  }
  if (fallbackUrl) {
    const path = extractStoragePath(fallbackUrl);
    if (path) {
      const { data, error } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(path, 60 * 60 * 24 * 7);
      if (!error && data?.signedUrl) return data.signedUrl;
    }
  }
  return fallbackUrl;
}

// --- Share ---

export interface SharedVideo {
  project_id: string;
  project_name: string | null;
  final_video_url: string;
  final_video_path: string | null;
  logo_url: string | null;
  created_at: string;
  is_promoted?: boolean;
  brand_agency_name?: string | null;
  brand_primary?: string | null;
  brand_secondary?: string | null;
  brand_logo_position?: string | null;
  brand_cta_label?: string | null;
  brand_cta_url?: string | null;
  brand_show_social?: boolean | null;
}

export async function ensureShareToken(projectId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('generate_imagemotion_share_token', {
    p_project_id: projectId,
  });

  if (!error && data?.share_token) return data.share_token;
  return null;
}

export async function fetchSharedVideo(shareToken: string): Promise<(SharedVideo & { audio_tracks?: AudioTrack[] }) | null> {
  const { data, error } = await supabase.rpc('get_shared_video', {
    p_share_token: shareToken,
  });

  if (error || !data || data.error) return null;

  const freshUrl = await getFreshVideoUrl(
    data.final_video_path,
    data.final_video_url
  );

  // Fetch active audio tracks for the project
  let tracks: AudioTrack[] = [];
  if (data.project_id) {
    const { data: trackData } = await supabase
      .from('audio_tracks')
      .select('id, track_type, label, audio_url, volume, start_offset_seconds, fade_in, fade_out, is_active, audio_path')
      .eq('project_id', data.project_id)
      .eq('is_active', true);

    if (trackData) {
      tracks = await Promise.all(
        (trackData as any[]).map(async (t) => {
          const freshAudioUrl = await refreshAudioUrl(t.audio_path, t.audio_url);
          return {
            id: t.id,
            track_type: t.track_type,
            label: t.label,
            audio_url: freshAudioUrl ?? t.audio_url,
            volume: t.volume,
            start_offset_seconds: t.start_offset_seconds,
            fade_in: t.fade_in,
            fade_out: t.fade_out,
            is_active: t.is_active,
          } as AudioTrack;
        })
      );
    }
  }

  return {
    ...data,
    final_video_url: freshUrl ?? data.final_video_url,
    audio_tracks: tracks,
  } as SharedVideo & { audio_tracks?: AudioTrack[] };
}

async function refreshAudioUrl(storagePath: string | null, fallbackUrl: string | null): Promise<string | null> {
  if (storagePath) {
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(storagePath, 60 * 60 * 24 * 7);
    if (!error && data?.signedUrl) return data.signedUrl;
  }
  return fallbackUrl;
}

// --- Share Analytics ---

function getVisitorId(): string {
  const key = 'im_visitor_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

function getSessionId(): string {
  const key = 'im_session_id';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
}

function detectDeviceType(): 'mobile' | 'tablet' | 'desktop' | 'unknown' {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android.*mobile|windows phone|blackberry/i.test(ua)) return 'mobile';
  if (/mobi/i.test(ua)) return 'mobile';
  return 'desktop';
}

function detectBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('OPR/') || ua.includes('Opera/')) return 'Opera';
  if (ua.includes('Chrome/') && !ua.includes('Edg/')) return 'Chrome';
  if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
  return 'Other';
}

function detectOS(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux') && !ua.includes('Android')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  return 'Other';
}

function cleanReferrer(): string | null {
  const ref = document.referrer;
  if (!ref) return null;
  try {
    const url = new URL(ref);
    if (url.hostname === window.location.hostname) return null;
    return url.hostname;
  } catch {
    return null;
  }
}

export async function recordShareAnalytics(
  shareToken: string,
  eventType: 'view' | 'play' | 'complete' | 'share_click',
  extra?: {
    watchDurationMs?: number;
    watchPercentage?: number;
    sharePlatform?: string;
  }
): Promise<void> {
  try {
    await supabase.rpc('record_share_analytics', {
      p_share_token: shareToken,
      p_event_type: eventType,
      p_visitor_id: getVisitorId(),
      p_session_id: getSessionId(),
      p_watch_duration_ms: extra?.watchDurationMs ?? null,
      p_watch_percentage: extra?.watchPercentage ?? null,
      p_share_platform: extra?.sharePlatform ?? null,
      p_referrer: cleanReferrer(),
      p_user_agent: navigator.userAgent,
      p_device_type: detectDeviceType(),
      p_browser: detectBrowser(),
      p_os: detectOS(),
      p_screen_width: window.screen.width,
      p_screen_height: window.screen.height,
      p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      p_language: navigator.language,
    });
  } catch {
    // Analytics should never break the viewer experience
  }
}

export interface ShareAnalyticsSummary {
  total_views: number;
  unique_viewers: number;
  total_plays: number;
  total_completions: number;
  total_shares: number;
  avg_watch_percentage: number;
  avg_watch_duration_ms: number;
  views_by_day: { day: string; views: number; unique_views: number; plays: number }[];
  top_referrers: { source: string; count: number }[];
  devices: { device: string; count: number }[];
  browsers: { browser: string; count: number }[];
  share_platforms: { platform: string; count: number }[];
  timezones: { timezone: string; count: number }[];
  first_view: string | null;
  last_view: string | null;
}

export async function fetchShareAnalytics(
  projectId: string
): Promise<ShareAnalyticsSummary | null> {
  const { data, error } = await supabase.rpc('fetch_share_analytics_summary', {
    p_project_id: projectId,
  });

  if (error || !data || data.error) return null;
  return data as ShareAnalyticsSummary;
}

// --- Brand Settings (user-level, shared across IM/VM) ---

import type { MediaBrandSettings, MediaBrandProfile, MediaBrandProfileInput, ActiveSpaceInfo, AudioTrack } from '@rehub/branded-viewer';
import type { AudioTrackRow } from '@rehub/branded-viewer';
import {
  getActiveSubBrand,
  getSubBrand,
  listMySubBrands,
  type SubBrand,
} from '@rehub/branding';

export type { MediaBrandSettings, MediaBrandProfile, MediaBrandProfileInput, ActiveSpaceInfo, AudioTrack, AudioTrackRow };

export async function fetchBrandSettings(userId: string): Promise<MediaBrandSettings | null> {
  const { data, error } = await supabase
    .from('media_brand_settings')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error || !data) return null;
  return data as MediaBrandSettings;
}

export async function upsertBrandSettings(
  userId: string,
  settings: Partial<Omit<MediaBrandSettings, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
): Promise<void> {
  const { error } = await supabase
    .from('media_brand_settings')
    .upsert(
      { user_id: userId, ...settings, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' }
    );

  if (error) throw error;
}

export async function uploadBrandLogo(file: File, userId: string): Promise<string> {
  const ext = file.name.split('.').pop() || 'png';
  const path = `brand-logos/${userId}/${Date.now()}.${ext}`;

  const { data, error } = await supabase.storage
    .from('imagemotion-uploads')
    .upload(path, file, { cacheControl: '31536000', upsert: false });

  if (error) throw error;

  const { data: signedData, error: signError } = await supabase.storage
    .from('imagemotion-uploads')
    .createSignedUrl(data.path, 60 * 60 * 24 * 365);

  if (signError || !signedData?.signedUrl) throw signError ?? new Error('Failed to create signed URL');
  return signedData.signedUrl;
}

// --- Media Brand Profiles (multi-brand) ---

/** Convert a SubBrand kit into the MediaBrandProfile shape used by IM. */
export function subBrandToMediaProfile(sb: SubBrand): MediaBrandProfile {
  return {
    id: `team-${sb.id}`,
    user_id: '',
    name: sb.agency_name || sb.name,
    agency_name: sb.agency_name,
    primary_color: sb.primary_color,
    secondary_color: sb.secondary_color,
    logo_url: sb.logo_url,
    logo_position: 'top-left',
    cta_label: sb.cta_label,
    cta_url: sb.cta_url,
    show_social_share: true,
    logo_intro_enabled: false,
    logo_outro_enabled: false,
    logo_bumper_duration: 3,
    logo_bumper_tagline: null,
    is_default: sb.is_default,
    created_at: '',
    updated_at: '',
  };
}

/**
 * Resolve the team kit a project should use:
 *   1. explicit `subBrandId` (per-project override), else
 *   2. the user's active sub-brand (workspace switcher), else
 *   3. null (caller falls back to media_brand_profiles).
 */
export async function fetchTeamSubBrandProfile(
  userId: string,
  subBrandId?: string | null
): Promise<MediaBrandProfile | null> {
  const kit = subBrandId
    ? await getSubBrand(supabase, subBrandId)
    : await getActiveSubBrand(supabase, userId);
  return kit ? subBrandToMediaProfile(kit) : null;
}

/**
 * List sub-brand kits the current user can pick from for an IM project.
 */
export async function fetchAvailableSubBrandProfiles(): Promise<MediaBrandProfile[]> {
  const list = await listMySubBrands(supabase);
  return list.map(subBrandToMediaProfile);
}

export async function fetchBrandProfiles(userId: string): Promise<MediaBrandProfile[]> {
  const { data, error } = await supabase
    .from('media_brand_profiles')
    .select('*')
    .eq('user_id', userId)
    .order('is_default', { ascending: false })
    .order('name', { ascending: true });

  if (error) { console.error('Fetch brand profiles error:', error); return []; }
  return (data ?? []) as MediaBrandProfile[];
}

export async function createBrandProfile(
  userId: string,
  input: MediaBrandProfileInput
): Promise<MediaBrandProfile> {
  if (input.is_default) {
    await supabase
      .from('media_brand_profiles')
      .update({ is_default: false })
      .eq('user_id', userId)
      .eq('is_default', true);
  }
  const { data, error } = await supabase
    .from('media_brand_profiles')
    .insert({ user_id: userId, ...input })
    .select()
    .single();

  if (error || !data) throw error ?? new Error('Failed to create brand profile');
  return data as MediaBrandProfile;
}

export async function updateBrandProfile(
  userId: string,
  id: string,
  input: MediaBrandProfileInput
): Promise<void> {
  if (input.is_default) {
    await supabase
      .from('media_brand_profiles')
      .update({ is_default: false })
      .eq('user_id', userId)
      .eq('is_default', true);
  }
  const { error } = await supabase
    .from('media_brand_profiles')
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function deleteBrandProfile(userId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('media_brand_profiles')
    .delete()
    .eq('id', id)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function applyBrandToProject(projectId: string, brandId: string | null): Promise<void> {
  const { error } = await supabase
    .from('imagemotion_projects')
    .update({ brand_profile_id: brandId })
    .eq('id', projectId);

  if (error) throw error;
}

export async function uploadBrandProfileLogo(file: File, userId: string): Promise<string> {
  const ext = file.name.split('.').pop() || 'png';
  const path = `brand-logos/${userId}/${Date.now()}.${ext}`;

  const { data, error } = await supabase.storage
    .from('imagemotion-uploads')
    .upload(path, file, { cacheControl: '31536000', upsert: false });

  if (error) throw error;

  const { data: signedData, error: signError } = await supabase.storage
    .from('imagemotion-uploads')
    .createSignedUrl(data.path, 60 * 60 * 24 * 365);

  if (signError || !signedData?.signedUrl) throw signError ?? new Error('Failed to create signed URL');
  return signedData.signedUrl;
}

// --- Active Space / Promotion ---

export async function fetchActiveSpaceInfo(userId: string): Promise<ActiveSpaceInfo> {
  const [countRes, limitRes] = await Promise.all([
    supabase.rpc('get_unified_active_slot_count', { p_user_id: userId }),
    supabase.rpc('get_unified_active_slot_limit', { p_user_id: userId }),
  ]);

  const countData = countRes.data as { total?: number } | null;
  const currentCount = countData?.total ?? 0;
  const limit = (limitRes.data as number) ?? 3;

  return { currentCount, limit, available: currentCount < limit };
}

export async function promoteToActiveSpace(
  projectId: string
): Promise<{ success: boolean; error?: string; current_count?: number; limit?: number }> {
  const { data, error } = await supabase.rpc('promote_media_to_active_space', {
    p_project_id: projectId,
    p_app_type: 'imagemotion',
  });

  if (error) return { success: false, error: error.message };
  return data as { success: boolean; error?: string; current_count?: number; limit?: number };
}

// --- Project Creation ---

export async function createProject(params: {
  userId: string;
  name: string;
  quality: ImagemotionQuality;
  serviceType?: ImagemotionServiceType;
  motionTierId?: string | null;
  motionTierVersionId?: string | null;
  motionPrompt?: string;
  nanoBananaPrompt?: string | null;
  klingMotionPrompt?: string | null;
  musicUrl: string | null;
  logoUrl: string | null;
  transitionType: string;
  settings: ProjectSettings;
  images: { url: string; path: string | null; orderIndex: number }[];
}): Promise<string> {
  const serviceType = params.serviceType ?? 'standard';
  // Motion-tier jobs must not sit in `queued` — the legacy clip worker still
  // polls that status and renders Kling 2.1 Standard. Stay in `editing` until
  // imagemotion-generate-motion claims the project (Kling 2.5).
  const initialStatus =
    serviceType === 'hyper_dusk' || params.motionTierId ? 'editing' : 'queued';

  const { data: project, error: projectError } = await supabase
    .from('imagemotion_projects')
    .insert({
      user_id: params.userId,
      name: params.name || null,
      status: initialStatus,
      quality: params.quality,
      service_type: serviceType,
      music_url: params.musicUrl,
      logo_url: params.logoUrl,
      transition_type: params.transitionType,
      settings: params.settings,
      motion_tier_id: params.motionTierId ?? null,
      motion_tier_version_id: params.motionTierVersionId ?? null,
    })
    .select()
    .single();

  if (projectError) throw new Error(getErrorMessage(projectError, 'Failed to create project'));

  const clipDuration = params.settings.clip_duration || SERVICES[serviceType].defaultClipDuration || 3.0;
  const motionPrompt =
    params.motionPrompt ??
    (serviceType === 'hyper_dusk' ? DEFAULT_HYPER_DUSK_MOTION_PROMPT : DEFAULT_MOTION_PROMPT);
  const nanoPrompt =
    serviceType === 'hyper_dusk'
      ? (params.nanoBananaPrompt ?? DEFAULT_HYPER_DUSK_NANO_PROMPT)
      : null;
  const klingPrompt =
    serviceType === 'hyper_dusk'
      ? (params.klingMotionPrompt ?? motionPrompt)
      : null;
  const imageRows = params.images.map((img) => ({
    project_id: project.id,
    image_url: img.url,
    image_path: img.path,
    source_image_url: img.url,
    source_image_path: img.path,
    order_index: img.orderIndex,
    clip_duration: clipDuration,
    motion_prompt: motionPrompt,
    nano_banana_prompt: nanoPrompt,
    kling_motion_prompt: klingPrompt,
    person_settings: null,
    generation_status: 'pending',
  }));

  const { error: imagesError } = await supabase
    .from('imagemotion_project_images')
    .insert(imageRows);

  if (imagesError) throw new Error(getErrorMessage(imagesError, 'Failed to create project images'));

  return project.id;
}

export async function createHyperDuskProject(params: {
  userId: string;
  name: string;
  image: { url: string; path: string | null };
  nanoPrompt?: string;
  klingPromptBase?: string;
  animationStyle?: HyperDuskAnimationStyle;
  animationDurationSeconds?: number;
  /**
   * Closest Kling-supported aspect ratio for the source image (16:9 /
   * 9:16 / 1:1). Passing this prevents Kling from stretching the source
   * to fit its default 16:9 canvas. Falls back to 16:9 if omitted, e.g.
   * for legacy callers.
   */
  aspectRatio?: AspectRatio;
}): Promise<string> {
  const duration = Math.max(5, Math.min(10, Math.round(params.animationDurationSeconds ?? 5)));
  const aspectRatio: AspectRatio = params.aspectRatio ?? '16:9';
  return createProject({
    userId: params.userId,
    name: params.name,
    quality: 'standard',
    serviceType: 'hyper_dusk',
    motionPrompt: params.klingPromptBase ?? DEFAULT_HYPER_DUSK_MOTION_PROMPT,
    nanoBananaPrompt: params.nanoPrompt ?? DEFAULT_HYPER_DUSK_NANO_PROMPT,
    klingMotionPrompt: params.klingPromptBase ?? DEFAULT_HYPER_DUSK_MOTION_PROMPT,
    musicUrl: null,
    logoUrl: null,
    transitionType: 'dissolve',
    settings: {
      transition_effect: 'dissolve',
      aspect_ratio: aspectRatio,
      output_resolution: 'hd',
      clip_duration: duration,
      filter: null,
      intro_text: null,
      outro_text: null,
      title_font_size: 64,
      logo_on_title_cards: false,
      logo_intro_card: false,
      logo_outro_card: false,
      logo_bumper_duration: 3,
      logo_bumper_tagline: null,
      logo_bumper_animation: 'fadeIn',
      logo_position: 'bottomRight',
      logo_scale: 0.3,
      logo_opacity: 0.8,
      music_volume: 0.7,
      music_effect: 'fadeInFadeOut',
      background_color: '#000000',
    },
    images: [{ ...params.image, orderIndex: 0 }],
  }).then(async (createdProjectId) => {
    const { error } = await supabase
      .from('imagemotion_project_images')
      .update({
        kling_animation_style: params.animationStyle ?? DEFAULT_HYPER_DUSK_ANIMATION_STYLE,
      })
      .eq('project_id', createdProjectId);
    if (error) throw error;
    return createdProjectId;
  });
}

// =========================================================
// Audio Tracks
// =========================================================

export async function fetchAudioTracks(projectId: string): Promise<AudioTrackRow[]> {
  const { data, error } = await supabase
    .from('audio_tracks')
    .select('*')
    .eq('project_id', projectId)
    .order('track_type', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Fetch audio tracks error:', error);
    return [];
  }

  const tracks = (data ?? []) as AudioTrackRow[];
  for (const track of tracks) {
    const fresh = await refreshAudioUrl(track.audio_path, track.audio_url);
    if (fresh) track.audio_url = fresh;
  }
  return tracks;
}

export async function uploadAudioTrack(
  file: File | Blob,
  trackType: 'music' | 'voiceover',
  userId: string,
  projectId: string,
  label?: string,
  ttsProvider?: string,
): Promise<AudioTrackRow> {
  const ext = file instanceof File ? (file.name.split('.').pop() || 'mp3') : 'webm';
  const storagePath = `audio/${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { data: uploadData, error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(storagePath, file, { cacheControl: '3600', upsert: false });

  if (uploadError) throw uploadError;

  const { data: signedData, error: signError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .createSignedUrl(uploadData.path, 60 * 60 * 24 * 365);

  if (signError || !signedData?.signedUrl) throw signError ?? new Error('Failed to sign URL');

  const { data: trackData, error: trackError } = await supabase
    .from('audio_tracks')
    .insert({
      project_id: projectId,
      user_id: userId,
      app_type: 'imagemotion',
      track_type: trackType,
      label: label || (trackType === 'music' ? 'Music track' : 'Voiceover'),
      audio_url: signedData.signedUrl,
      audio_path: storagePath,
      tts_provider: ttsProvider || (trackType === 'voiceover' ? 'recording' : null),
      is_active: true,
    })
    .select()
    .single();

  if (trackError) throw trackError;
  return trackData as AudioTrackRow;
}

export async function updateAudioTrack(
  trackId: string,
  updates: Partial<Pick<AudioTrackRow, 'volume' | 'fade_in' | 'fade_out' | 'is_active' | 'label'>>
): Promise<void> {
  const { error } = await supabase
    .from('audio_tracks')
    .update(updates)
    .eq('id', trackId);

  if (error) throw error;
}

export async function deleteAudioTrack(trackId: string): Promise<void> {
  const { error } = await supabase
    .from('audio_tracks')
    .delete()
    .eq('id', trackId);

  if (error) throw error;
}

export async function generateTTS(params: {
  text: string;
  provider: 'falai' | 'elevenlabs';
  voice_id?: string;
  project_id: string;
}): Promise<AudioTrackRow> {
  const { data, error } = await supabase.functions.invoke('generate-tts', {
    body: {
      text: params.text,
      provider: params.provider,
      voice_id: params.voice_id,
      project_id: params.project_id,
      app_type: 'imagemotion',
    },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data.track as AudioTrackRow;
}

// =========================================
// Admin
// =========================================

export type AdminRole = 'admin' | 'superadmin' | 'editor';

export const checkAdminStatus = async (userId: string): Promise<boolean> => {
  if (!isConfigured) return false;
  return checkAdminStatusShared(supabase, userId);
};

export const getAdminRole = async (userId: string): Promise<AdminRole | null> => {
  if (!isConfigured) return null;
  return getAdminRoleShared(supabase, userId) as Promise<AdminRole | null>;
};

export interface AdminImageMotionProject {
  id: string;
  name: string;
  status: string;
  quality: string;
  created_at: string;
  user_id: string | null;
  owner_email: string | null;
  image_count: number;
  share_token: string | null;
  is_promoted: boolean;
  is_listing_preview: boolean;
  final_video_url: string | null;
  final_video_path: string | null;
  error_message: string | null;
}

export interface AdminProjectImage {
  id: string;
  image_url: string;
  image_path: string | null;
  order_index: number;
}

export const getAdminProjectImages = async (projectId: string): Promise<AdminProjectImage[]> => {
  if (!isConfigured) return [];

  const { data, error } = await supabase
    .from('imagemotion_project_images')
    .select('id, image_url, image_path, order_index')
    .eq('project_id', projectId)
    .order('order_index', { ascending: true });

  if (error) {
    console.error('getAdminProjectImages error:', error);
    return [];
  }

  const images = (data ?? []) as AdminProjectImage[];

  const refreshed: AdminProjectImage[] = [];
  for (const img of images) {
    let url = img.image_url;
    if (img.image_path) {
      const { data: signedData } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(img.image_path, 60 * 60);
      if (signedData?.signedUrl) url = signedData.signedUrl;
    }
    refreshed.push({ ...img, image_url: url });
  }

  return refreshed;
};

export interface AdminImageMotionStats {
  total: number;
  uniqueUsers: number;
  completed: number;
  failed: number;
  inProgress: number;
  funnelPreviews: number;
}

export interface AdminImageMotionProjectsQuery {
  offset?: number;
  limit?: number;
  status?: string;
  search?: string;
}

const EMPTY_ADMIN_STATS: AdminImageMotionStats = {
  total: 0,
  uniqueUsers: 0,
  completed: 0,
  failed: 0,
  inProgress: 0,
  funnelPreviews: 0,
};

function projectCountQuery() {
  return supabase.from('imagemotion_projects').select('id', { count: 'exact', head: true });
}

async function countImageMotionProjects(
  refine: (query: ReturnType<typeof projectCountQuery>) => ReturnType<typeof projectCountQuery> = (query) => query,
): Promise<number> {
  const { count, error } = await refine(projectCountQuery());
  if (error) {
    console.error('countImageMotionProjects error:', error);
    return 0;
  }
  return count ?? 0;
}

/** Distinct owners across every project, not just the page on screen. */
async function countDistinctImageMotionUsers(): Promise<number> {
  const ids = new Set<string>();
  const pageSize = 1000;
  let from = 0;

  for (;;) {
    const { data, error } = await supabase
      .from('imagemotion_projects')
      .select('user_id')
      .not('user_id', 'is', null)
      .order('id', { ascending: true })
      .range(from, from + pageSize - 1);

    if (error) {
      console.error('countDistinctImageMotionUsers error:', error);
      break;
    }
    if (!data?.length) break;

    for (const row of data) {
      const id = (row as { user_id: string | null }).user_id;
      if (id) ids.add(id);
    }
    if (data.length < pageSize) break;
    from += pageSize;
  }

  return ids.size;
}

export const getAdminImageMotionStats = async (): Promise<AdminImageMotionStats> => {
  if (!isConfigured) return EMPTY_ADMIN_STATS;

  const [total, uniqueUsers, completed, failed, inProgress, funnelPreviews] = await Promise.all([
    countImageMotionProjects(),
    countDistinctImageMotionUsers(),
    countImageMotionProjects((query) => query.eq('status', 'completed')),
    countImageMotionProjects((query) => query.eq('status', 'failed')),
    countImageMotionProjects((query) => query.in('status', ['queued', 'generating_clips', 'rendering'])),
    countImageMotionProjects((query) => query.eq('is_listing_preview', true)),
  ]);

  return { total, uniqueUsers, completed, failed, inProgress, funnelPreviews };
};

export const getAdminImageMotionProjects = async (
  query: AdminImageMotionProjectsQuery = {},
): Promise<{ projects: AdminImageMotionProject[]; total: number }> => {
  if (!isConfigured) return { projects: [], total: 0 };

  const offset = Math.max(0, query.offset ?? 0);
  const limit = Math.min(Math.max(query.limit ?? 100, 1), 100);
  const status = query.status?.trim() ?? '';
  // PostgREST `.or()` treats commas and parentheses as syntax.
  const term = (query.search ?? '').trim().replace(/[(),%_]/g, ' ').replace(/\s+/g, ' ').trim();

  let ownerIds: string[] = [];
  if (term) {
    const { data: matchedProfiles, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .ilike('email', `%${term}%`)
      .limit(40);
    if (profileError) console.error('getAdminImageMotionProjects profile search error:', profileError);
    ownerIds = (matchedProfiles ?? []).map((p: { id: string }) => p.id).filter(Boolean);
  }

  let request = supabase
    .from('imagemotion_projects')
    .select('id, name, status, quality, created_at, user_id, share_token, is_promoted, is_listing_preview, final_video_url, final_video_path, error_message', { count: 'exact' })
    .order('created_at', { ascending: false })
    .order('id', { ascending: false });

  if (status) request = request.eq('status', status);
  if (term) {
    const ors = [`name.ilike.%${term}%`];
    if (ownerIds.length) ors.push(`user_id.in.(${ownerIds.join(',')})`);
    request = request.or(ors.join(','));
  }

  const { data: projects, error, count } = await request.range(offset, offset + limit - 1);

  if (error) {
    console.error('getAdminImageMotionProjects error:', error);
    return { projects: [], total: 0 };
  }
  if (!projects?.length) return { projects: [], total: count ?? 0 };

  const userIds = [...new Set(
    (projects as { user_id: string | null }[])
      .map((p) => p.user_id)
      .filter((id): id is string => !!id)
  )];
  const projectIds = (projects as { id: string }[]).map((p) => p.id);

  const { data: profiles } = userIds.length > 0
    ? await supabase.from('profiles').select('id, email').in('id', userIds)
    : { data: [] };

  const emailByUserId = new Map<string, string | null>();
  for (const p of profiles || []) {
    emailByUserId.set((p as { id: string }).id, (p as { email?: string }).email ?? null);
  }

  const imageCountByProject = new Map<string, number>();
  const imagePageSize = 1000;
  let imageFrom = 0;
  for (;;) {
    const { data: images, error: imagesError } = await supabase
      .from('imagemotion_project_images')
      .select('project_id')
      .in('project_id', projectIds)
      .order('id', { ascending: true })
      .range(imageFrom, imageFrom + imagePageSize - 1);
    if (imagesError) {
      console.error('getAdminImageMotionProjects images error:', imagesError);
      break;
    }
    if (!images?.length) break;
    for (const img of images) {
      const pid = (img as { project_id: string }).project_id;
      imageCountByProject.set(pid, (imageCountByProject.get(pid) ?? 0) + 1);
    }
    if (images.length < imagePageSize) break;
    imageFrom += imagePageSize;
  }

  const videoPaths = (projects as Array<Record<string, unknown>>)
    .map((p) => p.final_video_path as string | null)
    .filter((path): path is string => !!path);
  const signedUrlByPath = new Map<string, string>();
  if (videoPaths.length > 0) {
    const { data: signed } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrls(videoPaths, 60 * 60);
    for (const s of signed ?? []) {
      if (s.path && s.signedUrl) signedUrlByPath.set(s.path, s.signedUrl);
    }
  }

  return {
    projects: (projects as Array<Record<string, unknown>>).map((p) => ({
      id: p.id as string,
      name: (p.name as string) || 'Untitled',
      status: p.status as string,
      quality: (p.quality as string) || 'standard',
      created_at: p.created_at as string,
      user_id: (p.user_id as string) || null,
      owner_email: p.user_id ? emailByUserId.get(p.user_id as string) ?? null : null,
      image_count: imageCountByProject.get(p.id as string) ?? 0,
      share_token: (p.share_token as string) || null,
      is_promoted: !!p.is_promoted,
      is_listing_preview: !!p.is_listing_preview,
      final_video_url:
        signedUrlByPath.get(p.final_video_path as string) || (p.final_video_url as string) || null,
      final_video_path: (p.final_video_path as string) || null,
      error_message: (p.error_message as string) || null,
    })),
    total: count ?? projects.length,
  };
};

export interface AdminStagingAnimation {
  id: string;
  user_id: string;
  owner_email: string | null;
  project_name: string | null;
  source: string;
  status: string;
  style: string;
  duration_seconds: number;
  aspect_ratio: string;
  motion_level: string;
  model: string | null;
  credit_cost: number;
  charged: boolean;
  refunded: boolean;
  before_image_url: string | null;
  after_image_url: string | null;
  thumbnail_url: string | null;
  output_url: string | null;
  output_storage_path: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export const getAdminStagingAnimations = async (): Promise<AdminStagingAnimation[]> => {
  if (!isConfigured) return [];

  const { data: rows, error } = await supabase
    .from('transition_animations')
    .select('id, user_id, project_name, source, status, style, duration_seconds, aspect_ratio, motion_level, model, credit_cost, charged, refunded, before_image_url, after_image_url, thumbnail_url, output_url, output_storage_path, error_message, created_at, completed_at')
    .eq('transition_type', 'staging')
    .order('created_at', { ascending: false })
    .limit(300);

  if (error) {
    console.error('getAdminStagingAnimations error:', error);
    return [];
  }
  if (!rows?.length) return [];

  const userIds = [...new Set((rows as { user_id: string }[]).map((r) => r.user_id))];
  const { data: profiles } = await supabase.from('profiles').select('id, email').in('id', userIds);

  const emailByUserId = new Map<string, string | null>();
  for (const p of profiles || []) {
    emailByUserId.set((p as { id: string }).id, (p as { email?: string }).email ?? null);
  }

  return (rows as Omit<AdminStagingAnimation, 'owner_email'>[]).map((r) => ({
    ...r,
    owner_email: emailByUserId.get(r.user_id) ?? null,
  }));
};

export const getAdminStagingAnimationVideoUrl = async (
  animation: Pick<AdminStagingAnimation, 'output_url' | 'output_storage_path'>
): Promise<string | null> => {
  if (animation.output_storage_path) {
    const { data } = await supabase.storage
      .from(STORAGE_BUCKET)
      .createSignedUrl(animation.output_storage_path, 60 * 60);
    if (data?.signedUrl) return data.signedUrl;
  }
  return animation.output_url;
};
